import React, { useEffect } from 'react';
import { Form, Spinner } from 'react-bootstrap';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Button from 'components/DesignSystem/Button';
import DatePicker from 'components/DatePicker';
import RichTextEditor from 'components/RichTextEditor';
import RadioButton from 'components/DesignSystem/RadioButton';
import Snackbar from 'components/DesignSystem/Snackbar';
import AlertsCollection from 'components/AlertsCollection';
import { useLazyGetVideoDataByIdQuery } from 'services/api/videos';
import { onDatePickerChange } from 'helper/controlledField';
import useVideoSave from 'utils/hooks/videos/useVideoSave';
import {
  VideoProduct,
  videoProductToPath,
  videoProductToPermission,
} from 'helper/videos';

// Corpo do formulário — montado só quando o sheet abre (o Modal do BottomSheet
// desmonta os filhos ao fechar), garantindo estado limpo e Quill sem overhead
// enquanto fechado.
function FormSheetContent({
  editingId,
  product,
  onSaved,
}: {
  editingId: number | undefined;
  product: VideoProduct;
  onSaved: (message: string) => void;
}) {
  const [trigger, { data }] = useLazyGetVideoDataByIdQuery();
  const video = data?.data;

  const {
    isEditing,
    alerts,
    fileRef,
    tags,
    setTags,
    videoCategory,
    setVideoCategory,
    title,
    setTitle,
    date,
    setDate,
    dateWarning,
    vimeoIdText,
    onChangeVimeoId,
    richTextValue,
    setRichTextValue,
    videoCategories,
    updateFile,
    submitEnabled,
    submitVideoSave,
    isSaving,
  } = useVideoSave(product, video);

  useEffect(() => {
    if (editingId) {
      trigger({
        id: editingId,
        productPath: videoProductToPath[product],
        permissionType: videoProductToPermission[product],
      });
    }
  }, [editingId, product, trigger]);

  useEffect(() => {
    const success = alerts.find((alert) => alert.variant === 'success');
    if (success) {
      onSaved(success.message || 'Vídeo salvo');
    }
  }, [alerts, onSaved]);

  if (editingId && !video) {
    return (
      <div className="videos-mobile-loading">
        <Spinner />
      </div>
    );
  }

  return (
    <Form className="videos-form-sheet" onSubmit={submitVideoSave} noValidate>
      <div className="videos-form-sheet-fields">
        <Form.Group className="mb-3">
          <Form.Label>Vimeo ID</Form.Label>
          <Form.Control
            id="vimeoId"
            required
            type="text"
            value={vimeoIdText}
            onChange={onChangeVimeoId}
            disabled={isEditing}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Título</Form.Label>
          <Form.Control
            id="title"
            required
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Categoria do vídeo</Form.Label>
          <div className="videos-form-sheet-categories">
            {videoCategories.map((category) => (
              <RadioButton
                key={category.id}
                id={`category-${category.label}`}
                name="video-category"
                label={category.label}
                checked={videoCategory === category.id}
                onChange={() => setVideoCategory(category.id)}
              />
            ))}
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Data</Form.Label>
          <DatePicker
            id="date"
            required
            selected={date}
            className="form-control"
            onChange={onDatePickerChange(setDate)}
          />
          {dateWarning && (
            <Snackbar variant="warning" className="mt-2">{dateWarning}</Snackbar>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Tags</Form.Label>
          <Form.Control
            id="tags"
            type="text"
            value={tags}
            placeholder="chuva, temperatura, alertas…"
            onChange={(event) => setTags(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Arquivo (apresentação)</Form.Label>
          <Form.Control
            id="file"
            type="file"
            ref={fileRef}
            onChange={updateFile}
            className="rounded-3"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Descrição</Form.Label>
          <RichTextEditor
            id="description"
            value={richTextValue}
            onChange={setRichTextValue}
          />
        </Form.Group>

        <AlertsCollection alerts={alerts} />
      </div>

      <div className="videos-form-sheet-footer">
        <Button type="secondary" className="flex-fill">
          Salvar rascunho
        </Button>
        <Button
          submit
          disabled={!submitEnabled || isSaving}
          className="flex-fill"
        >
          {isSaving && <Spinner size="sm" />}
          {!isSaving && (isEditing ? 'Salvar' : 'Publicar')}
        </Button>
      </div>
    </Form>
  );
}

export default function FormSheet({
  open,
  editingId,
  product,
  dismiss,
  onSaved,
}: {
  open: boolean;
  editingId: number | undefined;
  product: VideoProduct;
  dismiss: () => void;
  onSaved: (message: string) => void;
}) {
  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      className="videos-form-bottom-sheet"
      title={editingId ? 'Editar vídeo' : 'Adicionar vídeo'}
    >
      <FormSheetContent
        key={editingId ?? 'new'}
        editingId={editingId}
        product={product}
        onSaved={onSaved}
      />
    </BottomSheet>
  );
}
